/**
 * Presigned-S3 upload helpers for prompt attachments — used by the agentic
 * room composer and (in future) any other surface that wants to feed files
 * into a pool execute call. Mirrors the contract the Run-tab `FileDropzone`
 * already speaks to `/networks/{id}/upload-url` so backend behavior is
 * identical: tmp-staged keys live under `input/logs/attachments/tmp/{id}/`
 * and the execute Lambda copies them to the per-execution prefix on start.
 *
 * Keeping this here (rather than re-implementing inside the composer)
 * means the existing Run-tab uploader can switch to it later without the
 * composer reaching into Run-tab internals.
 */
import { getRestApiBearerToken } from './apiToken';
import { attachmentNeedsTextract } from '../utils/runnerAttachments';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

/**
 * Generate a stable staging id used for one composer draft. The execute
 * Lambda matches this against `attachmentInput.attachments[].s3Key` so all
 * files for one Send share a prefix and get moved together.
 */
export function newAttachmentStagingId() {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  // RFC4122-ish fallback for jsdom and older browsers — not cryptographic,
  // just unique-enough for staging prefixes that the backend rewrites.
  return `stg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * GET /networks/{id}/upload-url
 *
 * @param {string} networkId
 * @param {{ filename: string, contentType?: string, stagingId?: string, prefix?: string }} opts
 * @returns {Promise<{ uploadUrl: string, s3Key: string, bucket?: string, requiredHeaders?: object }>}
 *   ``requiredHeaders`` = extra headers the presign SIGNED (e.g. ``x-amz-tagging`` on a staging
 *   upload) that the PUT MUST echo, or S3 returns SignatureDoesNotMatch (403).
 */
export async function getNetworkUploadUrl(networkId, opts) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const qs = new URLSearchParams();
  qs.set('filename', opts.filename || '');
  qs.set('contentType', opts.contentType || 'application/octet-stream');
  if (opts.stagingId) qs.set('stagingId', opts.stagingId);
  else if (opts.prefix) qs.set('prefix', opts.prefix);
  const res = await fetch(
    `${API_BASE_URL}/networks/${encodeURIComponent(networkId)}/upload-url?${qs.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(t || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * Presign + PUT a single file to S3 under the supplied staging id. Resolves
 * with the descriptor the agent runtime expects (`bucket`, `s3Key`,
 * `filename`, `contentType`, `size`, `needs_processing`).
 *
 * `onProgress` is invoked with `{ loaded, total, percent }` so the composer
 * can show per-chip progress.
 */
export async function uploadAttachmentForStaging(networkId, file, stagingId, { onProgress } = {}) {
  if (!file) throw new Error('uploadAttachmentForStaging: missing file');
  const contentType = file.type || 'application/octet-stream';
  const presign = await getNetworkUploadUrl(networkId, {
    filename: file.name,
    contentType,
    stagingId,
  });
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener('progress', (e) => {
      if (!onProgress) return;
      if (!e.lengthComputable) return;
      onProgress({ loaded: e.loaded, total: e.total, percent: Math.round((e.loaded / e.total) * 100) });
    });
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.open('PUT', presign.uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    // CRITICAL: the presign SIGNS any extra headers it added (e.g. ``x-amz-tagging`` on a
    // staging upload — get_upload_url tags ``stepflow-staging=tmp`` for lifecycle cleanup).
    // A signed header the PUT doesn't send → S3 ``SignatureDoesNotMatch`` (403). The workbench
    // FileDropzone applies these; the room composer path silently didn't — the cause of the
    // hours-long ".zip upload 403" (it was a signature mismatch, NOT an IAM/tag-permission issue).
    Object.entries(presign.requiredHeaders || {}).forEach(([k, v]) => {
      try { xhr.setRequestHeader(k, v); } catch (_e) { /* forbidden header names — skip */ }
    });
    xhr.send(file);
  });
  return {
    filename: file.name,
    contentType,
    bucket: presign.bucket || '',
    s3Key: presign.s3Key,
    size: file.size,
    needs_processing: attachmentNeedsTextract(contentType),
  };
}

/**
 * Build the `attachmentInput` body the execute endpoint expects from a list
 * of upload descriptors. Pure helper — kept separate so the composer / Run
 * tab can each shape attachments however they prefer and still hand back
 * a uniform payload.
 */
export function buildAttachmentInputBody(uploadedDescriptors) {
  const attachments = (uploadedDescriptors || []).map((a) => ({
    filename: a.filename,
    contentType: a.contentType,
    bucket: a.bucket,
    s3Key: a.s3Key,
    size: a.size,
    needs_processing: a.needs_processing,
  }));
  return {
    needs_async_processing: attachments.some((a) => a.needs_processing),
    attachments,
  };
}
